import 'core-js/stable'
import 'regenerator-runtime/runtime'
import 'array-flat-polyfill'
import React from 'react'
import { ApolloProvider } from '@apollo/client'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import * as Sentry from '@sentry/browser'
import { BrowserTracing } from '@sentry/tracing'

import ErrorBoundary from '@core/error-boundary'
import './style/index.scss'
import store from './store'
import debugUtil from './util/debug'
import App from './dayswaps'
import './favicons'
import packageInfo from '../package.json'
import { env } from '@app/const/config'
import { client } from '@app/util/apollo/apollo-client'

if (window.location.hostname !== 'localhost' && window.location.hostname !== 'app.local.dayswaps.top') {
  Sentry.init({
    dsn: 'https://d621519933a348b9923bcc10fc9781a1@o1306974.ingest.sentry.io/6550277',
    ignoreErrors: [
      // the following errors are reported by Sentry, but they're either out of our hands, or not really errors, so we ignore them
      // these are matched as substrings against both the message and 'type: message'. our own Sentry helpers report the message
      // as the exception type (so it ends up as the issue title), so the entries without the 'Error: ' prefix are what catch those
      'Error: Failed to fetch',
      'Failed to fetch',
      'TypeError: Load failed',
      'Load failed',
      'TypeError: NetworkError when attempting to fetch resource.',
      'Error: NetworkError when attempting to fetch resource.',
      'NetworkError when attempting to fetch resource.',
      'Error: Non-Error exception captured with keys: message',
      'Non-Error exception captured with keys: message',

      // There errors occur when user logs out or his token expires and frontend still tries connecting to websocket/requesting resources
      'Error: Response not successful: Received status code 401',
      'Response not successful: Received status code 401',
      // requests already in flight when the user logs out reach the server without a token
      'Error: Auth required',
      'Auth required',
      'Error: Socket closed with event 4500 Internal server error',
      'Socket closed with event 4500 Internal server error',

      'captureException: Non-Error exception captured with keys: message',
      'captureException: Object captured as exception with keys: message',
      'Error: collection.get no opts',
      'collection.get no opts',
      'Invariant Violation: 18 (see https://github.com/apollographql/invariant-packages)', // violation 18 means 'Store reset while query was in flight (not completed in link chain)', which only happens if user logs out while some request is running. not important.
      '18 (see https://github.com/apollographql/invariant-packages)'
    ],

    release: packageInfo.version,

    integrations: [
      new BrowserTracing(),
      new Sentry.Replay({
        maskAllText: false,
        maskAllInputs: false
      })
    ],

    // ignore 401 errors (expired auth tokens)
    beforeSend: (event, hint) => {
      if (hint.originalException) {
        if (hint.originalException.status === 401) {
          return null
        }
      }
      return event // otherwise, let errors be sent to Sentry
    },

    normalizeDepth: 10,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.2
  })
}

// The env-coloured favicon. `env` is 'dev' | 'stage' | 'prod', which is exactly how the
// fav-<env>-{16,32}.png files in app/favicons are named. This used to be derived from the
// hostname as host.split('.')[1] — which gives 'dev'/'stage' on app.dev/app.stage.dayswaps.top
// but 'dayswaps' on production's app.dayswaps.com, so both icons 404'd and the tab went blank.
const fav16 = document.getElementById('fav16')
const fav32 = document.getElementById('fav32')
fav16.href = `/favicons/fav-${env}-16.png`
fav32.href = `/favicons/fav-${env}-32.png`

const container = document.getElementById('ds-root')
const root = createRoot(container)
root.render(
  <ErrorBoundary>
    <Provider store={store}>
      <ApolloProvider client={client}>
        <App />
      </ApolloProvider>
    </Provider>
  </ErrorBoundary>
)

window.ds = debugUtil
